import * as React from "react";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Tabs, Tab, Box, Skeleton } from "@mui/material";
import { apiGroupDetail, apiPendingUsersWeb } from "@app/api";
import { screenPermission } from "@app/types";
import { Container, Helmet, TitleDetail } from "@app/components/atoms";
import {
  setIsRequestedGroupDetail,
  saveScreenPermission,
  store,
} from "@app/store";
import { useAppContext } from "@app/AppContext";

interface TabPanelProps {
  label: string;
  route: string;
  disabled?: boolean;
  className?: string;
}

type groupType = {
  groupId: string;
  groupName: string;
  companyName: string;
  machineCount: number;
  reportCount: number;
  location: string;
  memberCount: number;
  inspectionFormCount: number;
  logoUrl?: string;
};

export function GroupDetail() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { id } = useParams();
  const { setGroupName, setGroupLogoUrl, setMachineName, setMachineModelType } =
    useAppContext();
  const [titleDetail, setTitleDetail] = useState<groupType>();
  const [screenPermission, setScreenPermission] = useState<screenPermission>();
  const [metaTitle, setMetaTitle] = useState<string>("");
  const [pendingApprovals, setPendingApprovals] = useState<number>(0);
  const [memberSearch, setMemberSearch] = useState<string>("");

  const tabPanels: TabPanelProps[] = React.useMemo(
    () => [
      {
        label: t("groupDetailTab.reportHistoryTab"),
        route: `/groups/${id}/machines-reports-history`,
      },
      {
        label: t("groupDetailTab.machineTab"),
        route: `/groups/${id}/machines`,
      },
      {
        label: t("groupDetailTab.memberTab"),
        route: `/groups/${id}/members`,
        disabled: !screenPermission?.allowCreateEditDeleteMember,
        className: `${
          screenPermission?.allowCreateEditDeleteMember ? "" : "d-none-imp"
        }`,
      },

      {
        label: t("groupDetailTab.inspectionFormTab"),
        route: `/groups/${id}/inspection-form`,
        disabled: !screenPermission?.allowCreateEditDeleteInspectionForm,
        className: `${
          screenPermission?.allowCreateEditDeleteInspectionForm
            ? ""
            : "d-none-imp"
        }`,
      },
    ],
    [id, screenPermission, t],
  );

  useEffect(() => {
    if (!id) return;

    const fetchPendingApprovals = async () => {
      try {
        const res = await apiPendingUsersWeb(
          id,
          1,
          200,
          "DESC",
          "reporterName",
          memberSearch,
        );
        const { meta } = res.data;
        setPendingApprovals(meta.pageInfo.total);
      } catch (error) {
        console.error("Failed to fetch pending approvals:", error);
      }
    };

    fetchPendingApprovals();
  }, [id, memberSearch]);

  const route = React.useCallback(() => {
    let currentRoute = window.location.pathname;

    if (
      !currentRoute.includes("inspection-form") &&
      !currentRoute.includes("machines") &&
      !currentRoute.includes("members") &&
      !currentRoute.includes("machines-reports-history")
    ) {
      currentRoute = tabPanels[0].route;
    }

    return currentRoute;
  }, [tabPanels]);

  const [value, setValue] = useState(route);

  const handleChange = (_: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const getGroupDetail = (id: string) => {
    return apiGroupDetail(id).then((e) => {
      const { data, meta } = e.data;
      setGroupName(data?.groupName || "");
      setGroupLogoUrl(data?.logoUrl || "");
      setMachineName("");
      setMachineModelType("");
      setTitleDetail(data);
      setScreenPermission(meta.screenPermission);
      store.dispatch(saveScreenPermission(meta.screenPermission));
    });
  };

  useEffect(() => {
    getGroupDetail(`${id}`).finally(() => {
      dispatch(setIsRequestedGroupDetail(true));
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, dispatch]);

  useEffect(() => {
    if (!!titleDetail) {
      let metaKey = "groupDetailMachine";

      if (value.includes("inspection-form")) {
        metaKey = "groupDetailInspection";
      } else if (value.includes("machines-reports-history")) {
        metaKey = "groupDetailReportHistory";
      } else if (value.includes("members")) {
        metaKey = "groupDetailMembers";
      }

      setMetaTitle(
        t(`metaInfo.${metaKey}`, {
          groupName: titleDetail?.groupName,
        }),
      );
    } else {
      setMetaTitle("Operation assist");
    }
  }, [value, titleDetail, t]);

  useEffect(() => {
    setValue(route());
  }, [route]);

  useEffect(() => {
    return () => {
      dispatch(setIsRequestedGroupDetail(false));
      setGroupName("");
      setGroupLogoUrl("");
    };
  }, [dispatch, setGroupName, setGroupLogoUrl]);

  return (
    <div className="group-detail-page">
      <Helmet title={metaTitle} />
      <Container>
        <div className="row">
          <div className="sp-p24">
            {/* <NavigationItem title={t("groupList")} /> */}
            {!t("groupList") && <Skeleton width={100} height={20} />}

            {!titleDetail?.groupName ? (
              <div>
                {/* <Skeleton width={180} height={40} style={{ marginTop: 6 }} /> */}
                <div style={{ display: "flex", gap: "15px", marginTop: 6 }}>
                  <Skeleton width={50} height={20} />
                  <Skeleton width={60} height={20} />
                </div>
              </div>
            ) : (
              <TitleDetail
                // groupName={titleDetail?.groupName}
                location={titleDetail?.location}
                company={titleDetail?.companyName}
              />
            )}
          </div>
          <Box className="data-tabs" sx={{ width: "100%", marginTop: "24px" }}>
            {!titleDetail ? (
              <Box
                sx={{
                  display: "flex",
                  gap: 3,
                  paddingBottom: "8px",
                  marginTop: "24px",
                }}
              >
                <Skeleton variant="text" width={80} height={30} />
                <Skeleton variant="text" width={100} height={30} />
                <Skeleton variant="text" width={90} height={30} />
                <Skeleton variant="text" width={110} height={30} />
              </Box>
            ) : (
              <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                <Tabs
                  variant="scrollable"
                  value={value}
                  onChange={handleChange}
                  TabIndicatorProps={{ style: { background: "#140A9A" } }}
                  scrollButtons={false}
                >
                  {tabPanels.map((tab, index) => (
                    <Tab
                      key={index}
                      value={tab.route}
                      label={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          {tab.label}
                          {tab.route.includes("/members") &&
                            pendingApprovals > 0 && (
                              <span
                                style={{
                                  display: "inline-flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  minWidth: "17px",
                                  height: "17px",
                                  padding: "0 5.5px",
                                  borderRadius: "50%",
                                  backgroundColor: "#140a9a",
                                  color: "#fff",
                                  fontSize: "11px",
                                  fontWeight: "600",
                                  marginLeft: "6px",
                                }}
                              >
                                {pendingApprovals}
                              </span>
                            )}
                        </Box>
                      }
                      className={tab.className}
                      onClick={() => navigate(tab.route)}
                      disabled={tab.disabled}
                    />
                  ))}
                </Tabs>
              </Box>
            )}

            <div role="tabpanel">
              <Outlet
                context={{ memberSearch, setMemberSearch, setPendingApprovals }}
              />
            </div>
          </Box>
        </div>
      </Container>
    </div>
  );
}
